import {
  CLASS_ROSTER_LIMIT,
  useQueryClassesStudentsRoster,
} from "api/reactQuery/queries/classesStudents"
import { ClassRosterSource } from "api/reactQuery/queries/classesStudents.types"
import { IClassStoreItem } from "ts/interfaces/Class"

import { IStoreChargeStudent } from "./SendStoreChargeDialog.types"
import {
  getBalanceCheck,
  getLineStock,
  ICartLine,
} from "./SendStoreChargeDialog.utils"

interface IUseStoreChargeReviewArgs {
  isOpen: boolean
  isReviewStep: boolean
  classId: number
  source: ClassRosterSource
  students: IStoreChargeStudent[]
  isWholeClass: boolean
  excludedStudentIds: number[]
  studentCount: number
  cart: ICartLine[]
  costPerStudent: number
  items: IClassStoreItem[]
  isStockRefreshing: boolean
}

// Review's predictions: who gets charged (balance) and whether every cart line has the stock for them.
export const useStoreChargeReview = ({
  isOpen,
  isReviewStep,
  classId,
  source,
  students,
  isWholeClass,
  excludedStudentIds,
  studentCount,
  cart,
  costPerStudent,
  items,
  isStockRefreshing,
}: IUseStoreChargeReviewArgs) => {
  const withoutExcluded = (list: IStoreChargeStudent[]) =>
    list.filter((student) => !excludedStudentIds.includes(student.id))
  const loadedStudents = isWholeClass ? withoutExcluded(students) : students
  // Whole class beyond the loaded table page: fetch the roster on Review, or counts only above the page cap.
  const needsRoster = isWholeClass && loadedStudents.length < studentCount
  const isRosterFallback =
    needsRoster && studentCount + excludedStudentIds.length > CLASS_ROSTER_LIMIT
  const shouldFetchRoster = needsRoster && !isRosterFallback
  const {
    data: rosterResponse,
    isError: isRosterError,
    refetch: refetchRoster,
  } = useQueryClassesStudentsRoster({
    classId,
    source,
    options: {
      enabled: isOpen && isReviewStep && shouldFetchRoster,
      cacheTime: 0,
      staleTime: 0,
    },
  })
  const roster = shouldFetchRoster
    ? rosterResponse && withoutExcluded(rosterResponse.data.objects)
    : loadedStudents
  const isRosterPending = shouldFetchRoster && !roster && !isRosterError
  const isRosterFailed = shouldFetchRoster && !roster && isRosterError
  const balanceCheck =
    roster && !isRosterFallback ? getBalanceCheck(roster, costPerStudent) : null
  const predictedChargedCount = balanceCheck
    ? balanceCheck.chargedCount
    : studentCount
  const predictedSkippedCount = balanceCheck
    ? balanceCheck.rows.length - balanceCheck.chargedCount
    : 0
  const noneCanAfford = balanceCheck != null && predictedChargedCount === 0
  // Stock comes from the live items cache, not the cart's snapshot of the item.
  const isStockPending = isRosterPending || isStockRefreshing
  const lineStocks =
    (balanceCheck || isRosterFallback) && !isStockRefreshing
      ? cart.map((line) =>
          getLineStock(
            items.find((item) => item.id === line.item.id) ?? line.item,
            line.quantity,
            predictedChargedCount,
          ),
        )
      : null
  const shortLineCount = (lineStocks ?? []).filter(
    (stock) => stock.kind === "short" || stock.kind === "outOfStock",
  ).length
  // Fallback can't predict who's charged, so a short line only warns; the backend 409 is the backstop.
  const isStockBlocking = !isRosterFallback && shortLineCount > 0
  const isChargeBlocked =
    isStockPending || isRosterFailed || noneCanAfford || isStockBlocking

  return {
    isRosterFallback,
    isRosterPending,
    isRosterFailed,
    refetchRoster,
    balanceCheck,
    predictedChargedCount,
    predictedSkippedCount,
    noneCanAfford,
    isStockPending,
    lineStocks,
    shortLineCount,
    isStockBlocking,
    isChargeBlocked,
  }
}
